// ════════════════════════════════════════════════════════════
//  ui/Crown.jsx — THE KING'S CROWN (caP'n 2026-10-22: "a badge of sorts that only tourney winners get")
// ════════════════════════════════════════════════════════════
//  Every cup final won leaves a crown. Drawn in code (no hosted art — nothing to load, nothing to bill), five points on a band; the
//  bigger the cup the richer it gets: bone (Bottom vs Bottom, the Losers' Cup) → silver (the Contenders Cup, Last Chance) → gold (Top vs Top,
//  the Circuit Cup) → gold with gems (Salvation) → THE NIGHT CROWN, radiant → the Season Finale, white-gold. The tier is circuit.js crownTier.
//  <Crown tier size /> is the icon; <CrownTag C k /> is the little badge that sits beside a name (this week's crowns, from C.crowns).
//  Styles in league3.css (.cw-*). Never the ✦ star, never the heart — a crown is a crown.
// ════════════════════════════════════════════════════════════
import { CUPS, qualWinsOf } from "../online/circuit.js";

const cupName = (c) => (CUPS[c] ? CUPS[c].name.replace(/^the /, "") : c);

export function Crown({ tier = 1, size = 16, title }) {
  const t = Math.max(1, Math.min(8, Math.round(tier)));
  return (
    <svg className={`cw cw-t${t}`} width={size} height={Math.round(size * 0.72)} viewBox="0 0 24 17" role="img" aria-label={title || "Crown"}>
      {title ? <title>{title}</title> : null}
      <path className="cw-body" d="M2 14 L1 4 L7 9 L12 1.5 L17 9 L23 4 L22 14 Z" />
      <rect className="cw-band" x="2" y="14" width="20" height="2.6" rx="0.8" />
      {t >= 6 && <><circle className="cw-gem" cx="12" cy="6.2" r="1.5" /><circle className="cw-gem" cx="6.4" cy="10.4" r="1" /><circle className="cw-gem" cx="17.6" cy="10.4" r="1" /></>}
      {t >= 4 && <><circle className="cw-tip" cx="1" cy="3.6" r="1.1" /><circle className="cw-tip" cx="12" cy="1.3" r="1.2" /><circle className="cw-tip" cx="23" cy="3.6" r="1.1" /></>}
    </svg>
  );
}
/** "Won the Night Crown · Top vs Top ×2" — for a tooltip. cups = { cupKey: count }. */
export const crownsText = (cups) => "Won " + Object.entries(cups || {}).sort((a, b) => ((CUPS[b[0]] && CUPS[b[0]].tier) || 0) - ((CUPS[a[0]] && CUPS[a[0]].tier) || 0)).map(([c, n]) => `${cupName(c)}${n > 1 ? ` ×${n}` : ""}`).join(" · ");
/** A place on a division's table as a pill ("#3"): gold/silver/bronze for the top three, the Circuit's top three with a violet halo + gem (league3.css .isl-rk). */
export function RankPill({ div, pos, title }) {
  return <i className={"isl-rk dv-" + div + (pos <= 3 ? " m" + pos : "")} title={title}>#{pos}</i>;
}
/** The badge beside a name: this week's crowns (the biggest one's look, a count if more than one). Nothing if they've won no cup. */
/** THE Q (caP'n 2026-10-23: "a Q with purple black flames"): a Qualifier win. Drawn in code — a Q in a black-violet flame, nothing hosted. */
export function QMark({ n = 1, size = 16 }) {
  return (
    <span className="qm" title={n > 1 ? `Won ${n} Qualifiers this week` : "Won a Qualifier this week"} style={{ width: size, height: size }}>
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
        <path className="qm-f1" d="M12 0.5c1.2 3 5.2 4.8 5.2 9.2 0 1.2-.4 2.2-1 3 .1-1.700-.7-2.800-1.600-3.600-.5 2.300-2.400 3.100-2.400 5.500 0 .8.2 1.400.6 2C9 16.200 6.800 14.200 6.800 11 6.800 7.200 11 5 12 .5z" />
        <path className="qm-f2" d="M12 3c2.300 3.200 7.500 5.400 7.500 11a7.500 7.500 0 0 1-15 0c0-2.600 1.200-4.400 2.600-5.700.2 1.800 1.100 3 2.400 3.200C8.800 8 10.200 5.200 12 3z" />
        <text x="12" y="18" textAnchor="middle" className="qm-q">Q</text>
      </svg>
      {n > 1 ? <i>×{n}</i> : null}
    </span>
  );
}
const QCACHE = new WeakMap();
const qWins = (rows) => { if (!rows) return null; let m = QCACHE.get(rows); if (!m) { m = qualWinsOf(rows); QCACHE.set(rows, m); } return m; };
export function CrownTag({ C, k }) {
  const x = C && C.crowns && C.crowns.get(k);
  const qn = (qWins(C && C.rows) || new Map()).get(k) || 0;
  if (!x && !qn) return null;
  if (!x) return <span className="cw-tag"><QMark n={qn} /></span>;
  const title = x.night && x.night.lord ? "Night Lord" : x.night && x.night.champ ? "Night's Champion" : null;   // (the Night Crown's winner — one short word-pair beside the crown, nothing more)
  return <span className="cw-tag" title={crownsText(x.cups)}><Crown tier={x.tier} size={15} />{x.n > 1 ? <i>×{x.n}</i> : null}{title ? <em className={"cw-title" + (x.night.lord ? "" : " cw-champ")}>{title}</em> : null}{qn ? <QMark n={qn} /> : null}</span>;
}
